import {
  BillingHeader,
  BillingEmptyRow,
  BillingPagination,
  BillingStatus,
  BillingStatCard,
  BillingStatRow
} from './components/OrganizationBillingLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileInvoiceDollar,
  faHandHoldingUsd,
  faTrashAlt,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  getDefaultDashboardDateRange,
  toDashboardQueryDates
} from './components/adminDashboardUtils';
import { Alert, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import {
  useGetOrganizationBillingDashboardQuery,
  useGetOrganizationBillingCyclesQuery
} from 'rtk/org-admin/organizationBilling.slice';
import {
  formatBillingDate,
  formatUsdCents,
  pricingReasonLabel
} from 'util/organizationBillingFormat';
import { downloadBillingCycles } from 'util/organizationBillingDownload';
import BillingCycleSummary from './components/BillingCycleSummary';

const dataOf = value => value?.Data ?? value?.data ?? value;
const errorText = error =>
  typeof error?.data?.Data === 'string'
    ? error.data.Data
    : error?.Data ??
      error?.Message ??
      error?.error ??
      error?.message ??
      'Unable to load billing information.';

const AdminBillingDashboard = ({ dateRange }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [defaultRange] = useState(getDefaultDashboardDateRange);
  const range = dateRange || defaultRange;
  const dates = toDashboardQueryDates(range);
  const from = dates.startDate;
  const to = moment
    .utc(dates.endDate)
    .add(1, 'second')
    .format('YYYY-MM-DDTHH:mm:ss[Z]');
  useEffect(() => {
    setPage(0);
  }, [from, to]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const dashboard = useGetOrganizationBillingDashboardQuery(undefined, {
    refetchOnMountOrArgChange: true
  });
  const filters = {
    ...(from ? { fromutc: from } : {}),
    ...(to ? { toutc: to } : {})
  };
  const history = useGetOrganizationBillingCyclesQuery(
    {
      indexstart: page * pageSize,
      maxresults: pageSize,
      ...filters
    },
    { refetchOnMountOrArgChange: true }
  );
  const summary = dataOf(dashboard.data);
  const cycles = history.error ? null : dataOf(history.data);
  const projection = summary?.Projection;
  const exportCsv = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      await downloadBillingCycles(filters);
    } catch (error) {
      setDownloadError(errorText(error));
    } finally {
      setDownloading(false);
    }
  };
  return (
    <>
      <Card className="mb-3">
        <BillingHeader
          title="Organization billing"
          subtitle="Monitor hosted accounts, sponsorships, and billing activity."
          onRefresh={() => {
            dashboard.refetch();
            history.refetch();
          }}
          loading={dashboard.isFetching || history.isFetching}
        />
        <Card.Body className="px-4">
          {dashboard.isLoading && <Spinner animation="border" role="status" />}
          {dashboard.error && (
            <Alert variant="danger">{errorText(dashboard.error)}</Alert>
          )}
          {summary && (
            <>
              <BillingCycleSummary profile={summary.Profile} />
              {summary.Warnings?.map(message => (
                <Alert key={message} variant="warning">
                  {message}
                </Alert>
              ))}
              <Row className="g-3 mb-4">
                <Col xs={12} sm={6} lg={3}>
                  <BillingStatCard
                    label="Currently hosted notaries"
                    value={summary.HostedAccounts}
                    icon={faUserTie}
                    tone="primary"
                  >
                    <BillingStatRow
                      label="In free hosting period"
                      value={summary.GraceAccounts ?? 0}
                    />
                  </BillingStatCard>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <BillingStatCard
                    label="Scheduled for deletion"
                    value={summary.PendingDeletions ?? 0}
                    icon={faTrashAlt}
                    tone="warning"
                  >
                    <span className="text-600">
                      Still included in hosted notaries until access ends.
                    </span>
                  </BillingStatCard>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <BillingStatCard
                    label="Sponsored subscriptions"
                    value={
                      (projection?.SponsoredQualifyingQuantity ?? 0) +
                      (summary.NonQualifyingSponsorships ?? 0)
                    }
                    icon={faHandHoldingUsd}
                    tone="info"
                  >
                    <BillingStatRow
                      label="Eligible for group pricing"
                      value={projection?.SponsoredQualifyingQuantity ?? 0}
                    />
                    <BillingStatRow
                      label="Individual pricing"
                      value={summary.NonQualifyingSponsorships ?? 0}
                    />
                    <span className="text-600">
                      Charged separately on each notary’s subscription renewal
                      cadence, outside the hosted billing cycle. Renewals are
                      charged 7 days before monthly or 14 days before annual
                      coverage ends.
                    </span>
                  </BillingStatCard>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <BillingStatCard
                    label="Unpaid balance"
                    value={formatUsdCents(summary.OutstandingCents)}
                    icon={faFileInvoiceDollar}
                    tone={summary.OutstandingCents > 0 ? 'danger' : 'success'}
                  >
                    <span className="text-600">
                      {summary.OpenInvoices ?? 0} unpaid{' '}
                      {summary.OpenInvoices === 1 ? 'invoice' : 'invoices'}
                    </span>
                  </BillingStatCard>
                </Col>
              </Row>
              <div
                role="region"
                aria-label="Hosted charge estimate"
                className="border rounded-3 overflow-hidden p-0"
              >
                <Row className="g-0">
                  <Col xs={12} lg={9} className="p-3">
                    <h5 className="mb-1">
                      Estimated hosted charges this cycle
                    </h5>
                    <p className="text-600 fs--1 mb-2">
                      Your hosted-data invoice is calculated when the billing
                      cycle ends. Notaries hosted at any point in this cycle are
                      billed for the full cycle, including any removed during
                      it.
                    </p>
                    {projection?.RequiresPricingReview && (
                      <Alert variant="warning">
                        An approved price is needed. The projection is
                        incomplete.
                      </Alert>
                    )}
                    {!projection?.Lines?.length && (
                      <p className="text-600 mb-0">
                        No billable hosted accounts in this cycle.
                      </p>
                    )}
                    {projection?.Lines?.map((line, index) => (
                      <div key={`${line.ProductCode}-${index}`}>
                        <span className="fw-semi-bold text-900">
                          Hosted data
                        </span>
                        <Row as="dl" className="g-2 mt-0 mb-1">
                          <Col xs={6} sm="auto" className="me-sm-4">
                            <dt className="text-600 fs--1 fw-normal mb-1">
                              Billable notaries
                            </dt>
                            <dd className="text-900 fs-1 fw-semi-bold mb-0">
                              {line.Quantity}
                            </dd>
                          </Col>
                          <Col xs={6} sm="auto">
                            <dt className="text-600 fs--1 fw-normal mb-1">
                              Rate per notary
                            </dt>
                            <dd className="text-900 fs-1 fw-semi-bold mb-0">
                              {line.RequiresPricingReview
                                ? 'Pending pricing'
                                : formatUsdCents(line.UnitAmountCents)}
                            </dd>
                          </Col>
                        </Row>
                        <p className="text-600 fs--1 mb-0">
                          {pricingReasonLabel(line)}
                        </p>
                      </div>
                    ))}
                  </Col>
                  <Col
                    xs={12}
                    lg={3}
                    className="bg-soft-primary p-3 d-flex flex-column justify-content-center"
                  >
                    <strong className="text-900 fs-2">
                      <span className="d-block text-600 fs--1 fw-normal mb-1">
                        Estimated total:{' '}
                      </span>
                      {summary.ProjectionTotalCents == null
                        ? 'Pending pricing'
                        : formatUsdCents(summary.ProjectionTotalCents)}
                    </strong>
                    <p className="text-600 fs--1 mt-1 mb-0">
                      Hosted accounts only.
                    </p>
                  </Col>
                </Row>
                <div className="border-top px-3 py-2 text-600 fs--1">
                  Sponsored subscriptions are charged separately on each
                  notary's renewal cadence.
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      <Card>
        <BillingHeader
          title="Billing cycle history"
          subtitle="Review previous billing cycles and export your records."
        />
        <Card.Body className="px-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <p className="mb-0 text-600 fs--1">
              History follows the dashboard date range.
            </p>
            <Button
              variant="falcon-default"
              size="sm"
              onClick={exportCsv}
              disabled={downloading || Boolean(from && to && from >= to)}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              {downloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
          {from && to && from >= to && (
            <Alert variant="warning">
              The end date must be after the start date.
            </Alert>
          )}
          {downloadError && <Alert variant="danger">{downloadError}</Alert>}
          {history.isLoading && <Spinner animation="border" role="status" />}
          <BillingPagination
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size);
              setPage(0);
            }}
            data={cycles}
            onRefresh={() => history.refetch()}
            loading={history.isLoading || history.isFetching}
            error={history.error}
          />
          <div className="border rounded-3 overflow-hidden">
            <Table
              responsive
              hover
              className="fs--1 mb-0 align-middle table-borderless"
            >
              <thead className="bg-200 text-900 text-nowrap align-middle">
                <tr>
                  <th scope="col" className="ps-3">
                    Invoice
                  </th>
                  <th scope="col">Cycle</th>
                  <th scope="col">Status</th>
                  <th scope="col">Settlement</th>
                  <th scope="col" className="text-end">
                    Total
                  </th>
                  <th scope="col" className="text-end pe-3">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="border-top">
                {!history.isLoading && !cycles?.Objects?.length && (
                  <BillingEmptyRow colSpan={6}>
                    No billing cycles found for the selected date range.
                  </BillingEmptyRow>
                )}
                {cycles?.Objects?.map(cycle => (
                  <tr key={cycle.GUID} className="border-bottom">
                    <td className="ps-3 text-600">
                      {cycle.InvoiceNumber ?? 'Not issued'}
                    </td>
                    <td className="fw-semi-bold text-900">
                      {formatBillingDate(cycle.CycleStartUtc)} {'–'}{' '}
                      {formatBillingDate(cycle.CycleEndUtc)}
                    </td>
                    <td>
                      <BillingStatus>{cycle.Status}</BillingStatus>
                    </td>
                    <td>
                      {cycle.InvoiceStatus ? (
                        <BillingStatus>{cycle.InvoiceStatus}</BillingStatus>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-end fw-semi-bold text-900">
                      {cycle.TotalCents == null
                        ? 'Pending pricing'
                        : formatUsdCents(cycle.TotalCents)}
                    </td>
                    <td className="text-end pe-3 text-600 text-nowrap">
                      {formatBillingDate(cycle.CreatedUtc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

AdminBillingDashboard.propTypes = {
  dateRange: PropTypes.shape({
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired
  })
};

export default AdminBillingDashboard;
